import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Icon, SimpleFlexbox } from 'ctm-components';
import { onReplaceLinkClick, pageContext } from 'ctm-util-functions';
import { splitTrafficIdSelector } from 'ctm-selectors';
import { applicantFirstName, getLabelFromCode, getColourLabel } from '../../core/state_selectors';
import { EditCarDetailsItem } from './edit_car_details_item';
import styles from './edit_car_details.css';

/**
 * Car details edit page component
 * @param {String} props - the component's props object
 * @param {String} props.firstName - the applicant firstName
 * @param {String} props.make - the make of the car
 * @param {String} props.year - the year of the car
 * @param {String} props.model - the model of the car
 * @param {String} props.body - the body of the car
 * @param {String} props.transmission - the transmission of the car
 * @param {String} props.fuel - the fuel type of the car
 * @param {String} props.description - the car's long description text
 * @param {String} props.colour - the colour of the car
 * @returns {Node} the edit car details page component
 * @constructor
 */

const onClick = (event) => {
    const ctmRedirect = 'car_colour';
    const chooRedirect = 'factory_options';
    const redirectTarget = pageContext.getProps().everestTheme === 'choo' ? chooRedirect : ctmRedirect;
    onReplaceLinkClick(event, `/car-insurance/journey/${redirectTarget}`);
};

const _EditCarDetails = ({ firstName, make, year, model, body, transmission, fuel, description, colour, hideColour, splitTrafficId, getSelectedSeries }) => {
    if (!getSelectedSeries) return null;

    return (
        <SimpleFlexbox className={styles.root} display="flex" wrap>
            <SimpleFlexbox xs={12}>
                <SimpleFlexbox display="flex">
                    <a className={styles.back_button} data-id="edit_car.backButton" href="#" onClick={(e) => onClick(e)}>
                        <Icon variant="ctm_angle_left" margin="right" />
                        Back
                    </a>
                    <h2 className={styles.title}>Edit your car details.</h2>
                </SimpleFlexbox>
            </SimpleFlexbox>
            <SimpleFlexbox xs={12} sm={6}>
                <EditCarDetailsItem identifier="make" value={make} />
                <EditCarDetailsItem identifier="year" value={year} />
                <EditCarDetailsItem identifier="model" value={model} />
                <EditCarDetailsItem identifier="body" value={body} splitTrafficId={splitTrafficId} />
            </SimpleFlexbox>
            <SimpleFlexbox xs={12} sm={6}>
                <EditCarDetailsItem identifier="transmission" value={transmission} splitTrafficId={splitTrafficId} />
                <EditCarDetailsItem identifier="fuel" value={fuel} splitTrafficId={splitTrafficId} />
                <EditCarDetailsItem identifier="series" value={getSelectedSeries?.descriptions} splitTrafficId={splitTrafficId} />
                {!hideColour && <EditCarDetailsItem identifier="colour" value={colour} />}
            </SimpleFlexbox>
        </SimpleFlexbox>
    );
};

_EditCarDetails.propTypes = {
    firstName: PropTypes.string.isRequired,
    make: PropTypes.string.isRequired,
    year: PropTypes.string.isRequired,
    model: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    transmission: PropTypes.string.isRequired,
    fuel: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    colour: PropTypes.string.isRequired,
    hideColour: PropTypes.bool,
    updatedDescription: PropTypes.object
};

/**
 * Map state to our component props
 * @param {Object} state - the application state
 * @returns {Object} - the set of mapped props for the component
 */
const mapStateToProps = (state) => {
    const colour = getColourLabel(state);
    const hideColour = `${colour}` === '';
    const splitTrafficId = splitTrafficIdSelector(state);
    const currentSeries = state.getIn(['vertical', 'quote', 'form', 'coverDetail', 'vehicle', 'redbookCode']) || '';
    const currentBadge = state.getIn(['vertical', 'quote', 'form', 'coverDetail', 'vehicle', 'badge']) || '';
    const descriptionResponse = JSON.parse(JSON.stringify(state.getIn(['vertical', 'quote', 'apiResponses', 'series']))) || [];
    const getSelectedSeries = Array.isArray(descriptionResponse)
        ? descriptionResponse.find((series) => {
            return series.redbookCode === JSON.parse(JSON.stringify(currentSeries));
        }) || null
        : null;
    return {
        firstName: applicantFirstName({ state }),
        make: getLabelFromCode(state, 'coverDetail.vehicle.make', 'vertical.quote.apiResponses.makes'),
        year: getLabelFromCode(state, 'coverDetail.vehicle.year', 'vertical.quote.apiResponses.years'),
        model: getLabelFromCode(state, 'coverDetail.vehicle.model', 'vertical.quote.apiResponses.models'),
        body: getLabelFromCode(state, 'coverDetail.vehicle.body', 'vertical.quote.apiResponses.bodies'),
        transmission: getLabelFromCode(state, 'coverDetail.vehicle.transmission', 'vertical.quote.apiResponses.transmissions'),
        fuel: getLabelFromCode(state, 'coverDetail.vehicle.fuel', 'vertical.quote.apiResponses.fuel'),
        colour,
        description: state.getIn('vertical.quote.apiResponses.description'.split('.')),
        hideColour,
        splitTrafficId,
        getSelectedSeries
    };
};

const EditCarDetails = connect(mapStateToProps)(_EditCarDetails);

export { EditCarDetails, _EditCarDetails };
